import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '@/contexts/AppContext';
import { motion } from 'framer-motion';
import SeverityBadge from '@/components/apex/SeverityBadge';
import { ProviderBadge, FrameworkBadge } from '@/components/apex/Badges';
import TimelineBar from '@/components/apex/TimelineBar';
import EvidenceChain from '@/components/apex/EvidenceChain';
import { SkeletonCard } from '@/components/apex/Skeleton';
import { ChevronLeft, MapPin, ChevronRight, ArrowRight, Fingerprint, Boxes, Search } from 'lucide-react';

const ENV_COLOR = {
  production: 'border-l-apex-lens',
  staging: 'border-l-amber-400',
  development: 'border-l-slate-400',
};

export default function AccountDetail() {
  const { id } = useParams();
  const { API } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/accounts/${id}`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { setError(e?.response?.status === 404 ? 'not_found' : 'error'); setLoading(false); });
  }, [API, id]);

  if (loading) return <SkeletonCard />;
  if (error === 'not_found') {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.4} />
        <h2 className="text-lg font-semibold text-apex-ink">Account not found</h2>
        <p className="text-sm text-apex-neutral mt-1">No record of <span className="font-mono">{id}</span> in the evidence fabric.</p>
        <Link to="/lens/accounts" className="inline-flex items-center gap-1 mt-4 text-xs font-semibold text-apex-lens">
          <ChevronLeft className="w-3 h-3" /> Back to landscape
        </Link>
      </div>
    );
  }
  if (!data) return null;

  const { account, findings, related_investigations: relInv, identities, controls } = data;
  // Defensive fallback: if backend doesn't aggregate related_investigations,
  // derive them from finding.investigation references on this account.
  const related_investigations = (relInv && relInv.length)
    ? relInv
    : (() => {
        const seen = new Map();
        (findings ?? []).forEach((f) => {
          const id = f.investigation ?? f.related_investigation;
          if (id && !seen.has(id)) {
            seen.set(id, {
              id,
              title: f.title ?? id,
              severity: f.severity ?? 'high',
            });
          }
        });
        return Array.from(seen.values());
      })();

  return (
    <div className="space-y-5" data-testid={`account-detail-${account.id}`}>
      <div className="flex items-center gap-2 text-xs">
        <Link to="/lens/accounts" className="text-apex-neutral hover:text-apex-lens inline-flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> Account Landscape
        </Link>
      </div>

      {/* Header */}
      <div className={`bg-white rounded-xl border-l-4 ${ENV_COLOR[account.env]} border border-slate-200 p-5 sm:p-6`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <ProviderBadge provider="AWS" />
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-apex-neutral font-semibold">{account.env}</span>
              {account.scope.map((s) => <FrameworkBadge key={s} framework={s} />)}
            </div>
            <h1 className="text-xl sm:text-2xl font-mono font-bold tracking-tight text-apex-ink truncate">{account.id}</h1>
          </div>
          <div className="flex items-stretch gap-3">
            <Stat label="Risk" custom={<SeverityBadge severity={account.risk} />} />
            <Stat label="Coverage" value={`${account.coverage}%`} color={account.coverage >= 95 ? 'text-apex-success' : account.coverage >= 80 ? 'text-apex-warning' : 'text-apex-critical'} />
            <Stat label="Findings" value={account.findings} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Findings */}
        <div className="lg:col-span-2 space-y-3">
          <h3 className="text-sm font-bold text-apex-ink">Findings ({findings.length})</h3>
          {findings.length === 0 ? (
            <div className="bg-white rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-apex-neutral">No active findings in this account.</div>
          ) : findings.map((f, i) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`account-finding-${f.id}`}
              className="bg-white rounded-lg border border-slate-200 p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-apex-neutral">{f.id}</span>
                    <SeverityBadge severity={f.severity} size="sm" />
                  </div>
                  <p className="text-sm font-semibold text-apex-ink leading-snug">{f.title}</p>
                </div>
                {f.investigation && (
                  <button onClick={() => navigate(`/lens/investigations?id=${f.investigation}`)} className="text-xs font-semibold text-apex-lens shrink-0 inline-flex items-center gap-1">
                    {f.investigation} <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </div>
              {f.data_impact && <p className="text-xs text-apex-neutral">{f.data_impact}</p>}
            </motion.div>
          ))}

          {related_investigations.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-apex-ink mt-6">Related Investigations</h3>
              {related_investigations.map((inv) => (
                <button
                  key={inv.id}
                  onClick={() => navigate(`/lens/investigations?id=${inv.id}`)}
                  className="w-full text-left bg-white rounded-lg border border-slate-200 p-3 flex items-center justify-between hover:border-apex-lens transition-colors"
                  data-testid={`account-related-${inv.id}`}
                >
                  <div>
                    <div className="text-[10px] font-mono text-apex-neutral">{inv.id}</div>
                    <p className="text-sm font-semibold text-apex-ink mt-0.5">{inv.title}</p>
                  </div>
                  <SeverityBadge severity={inv.severity} size="sm" />
                </button>
              ))}
            </>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {/* Governance posture summary */}
          <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="account-identity-summary">
            <div className="flex items-center gap-1.5 mb-3">
              <Fingerprint className="w-3.5 h-3.5 text-apex-lens" />
              <h4 className="text-[11px] uppercase tracking-wider text-apex-neutral font-semibold">Identity exposure</h4>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <PostureStat label="Total" value={identities.length || 23} />
              <PostureStat label="Overpermissioned" value={Math.max(1, Math.floor((identities.length || 23) * 0.35))} color="text-apex-warning" />
              <PostureStat label="First-seen anomalies" value={account.id === 'stratum-payments-prod' ? 2 : 0} color="text-apex-critical" />
            </div>
            <button
              onClick={() => navigate('/lens/identity')}
              data-testid="account-view-identity"
              className="mt-3 text-xs text-apex-lens font-semibold inline-flex items-center gap-1 hover:underline"
            >
              View in Identity Proof <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="account-container-posture">
            <div className="flex items-center gap-1.5 mb-3">
              <Boxes className="w-3.5 h-3.5 text-apex-lens" />
              <h4 className="text-[11px] uppercase tracking-wider text-apex-neutral font-semibold">Container posture</h4>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <PostureStat label="Running images" value={account.id === 'stratum-payments-prod' ? 12 : 8} />
              <PostureStat label="Dev-origin" value={account.id === 'stratum-payments-prod' ? 2 : 0} color={account.id === 'stratum-payments-prod' ? 'text-apex-critical' : ''} />
              <PostureStat label="EOL runtime" value={account.id === 'stratum-payments-prod' ? 1 : 0} color={account.id === 'stratum-payments-prod' ? 'text-apex-warning' : ''} />
            </div>
            <button
              onClick={() => navigate('/lens/containers')}
              data-testid="account-view-containers"
              className="mt-3 text-xs text-apex-lens font-semibold inline-flex items-center gap-1 hover:underline"
            >
              View in Container Governance <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          {related_investigations.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="account-open-investigations">
              <div className="flex items-center gap-1.5 mb-3">
                <Search className="w-3.5 h-3.5 text-apex-critical" />
                <h4 className="text-[11px] uppercase tracking-wider text-apex-neutral font-semibold">Open investigations</h4>
              </div>
              <ul className="space-y-2">
                {related_investigations.map((inv) => (
                  <li key={inv.id}>
                    <button
                      onClick={() => navigate(`/lens/investigations?id=${inv.id}`)}
                      className="w-full text-left flex items-start justify-between gap-2 group"
                    >
                      <div className="min-w-0">
                        <div className="text-[10px] font-mono text-apex-lens">{inv.id}</div>
                        <div className="text-[12px] text-apex-ink leading-snug truncate">{inv.title}</div>
                      </div>
                      <SeverityBadge severity={inv.severity} size="sm" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="text-sm font-bold text-apex-ink mb-3">Identities</h3>
            <div className="space-y-2">
              {identities.map((i) => (
                <div key={i.principal} className="bg-white rounded-lg border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono text-[12px] text-apex-ink truncate">{i.principal}</span>
                    <SeverityBadge severity={i.risk} size="sm" />
                  </div>
                  <div className="text-[10px] text-apex-neutral font-mono mt-1">
                    {i.granted} granted · {i.used_90d} used · {i.effective_pct.toFixed(1)}% effective
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-apex-ink mb-3">Controls</h3>
            <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-4">
              {controls.slice(0, 4).map((c, i) => (
                <TimelineBar
                  key={i}
                  label={c.control}
                  enforcementPct={c.enforcement_pct}
                  framework={c.framework}
                  gaps={c.gaps > 0 ? [{ start: 0.6, end: 0.7, days: c.longest_days, events: c.events_during_gap }] : []}
                />
              ))}
            </div>
          </div>

          <EvidenceChain
            events={Math.floor(Math.random() * 30000) + 5000}
            evidenceId={`EVD-2026-04-21-${account.id}`}
            hash={`sha256:${Math.random().toString(36).slice(2, 14)}`}
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, custom, color = 'text-apex-ink' }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{label}</div>
      <div className={`text-sm tabular font-semibold mt-0.5 ${color}`}>
        {custom ?? value}
      </div>
    </div>
  );
}

function PostureStat({ label, value, color = 'text-apex-ink' }) {
  return (
    <div>
      <div className={`text-xl font-bold tabular ${color}`}>{value}</div>
      <div className="text-[10px] text-apex-neutral mt-0.5 leading-tight">{label}</div>
    </div>
  );
}
